import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import {
  X,
  Mail,
  FolderKanban,
  FileEdit,
  Sparkles,
  Reply,
  ReplyAll,
  Forward,
  ExternalLink,
  Clock,
  CheckCircle2,
  Paperclip,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api, ApiError } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import { cn } from '@/lib/cn';
import { relativeTime, shortDate } from '@/lib/format';
import { CLASSIFICATION_META, TAG_LABELS } from '@/lib/inbox';
import { EmailBody } from '@/components/email/EmailBody';
import { ThreadSummaryCard } from '@/components/email/ThreadSummaryCard';
import { DraftComposer } from '@/components/drafts/DraftComposer';
import type { DraftKind } from '@cc/types';

const AUTODISMISS_MS = 30_000;

interface Props {
  id: string;
  onClose: () => void;
  onSelectMessage?: (id: string) => void;
}

export function MessagePanel({ id, onClose, onSelectMessage }: Props) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: qk.message(id),
    queryFn: () => api.getMessage(id),
  });
  const contextQuery = useQuery({
    queryKey: [...qk.message(id), 'context'],
    queryFn: () => api.getMessageContext(id),
    enabled: Boolean(data),
  });

  const [composingDraftId, setComposingDraftId] = useState<string | null>(null);
  // After Send, we keep the composer mounted so the thumbs prompt is visible.
  // sentAt is the timestamp; an effect auto-dismisses after AUTODISMISS_MS unless
  // the user already rated or manually closed.
  const [sentAt, setSentAt] = useState<number | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (composingDraftId && composerRef.current) {
      composerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [composingDraftId]);

  // Auto-dismiss the post-send composer after ~30s.
  useEffect(() => {
    if (!sentAt || !composingDraftId) return;
    const handle = window.setTimeout(() => {
      setComposingDraftId(null);
      setSentAt(null);
      void queryClient.invalidateQueries({ queryKey: qk.message(id) });
    }, AUTODISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [sentAt, composingDraftId, id, queryClient]);

  const compose = useMutation({
    mutationFn: (kind: DraftKind) => api.createDraft({ kind, sourceMessageId: id }),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['drafts'] });
      toast.success(res.existed ? 'Existing draft opened' : 'Draft created');
      setComposingDraftId(res.draft.id);
      setSentAt(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not create draft';
      toast.error(msg);
    },
  });

  function dismissComposer() {
    setComposingDraftId(null);
    setSentAt(null);
    void queryClient.invalidateQueries({ queryKey: qk.message(id) });
  }

  function markSent() {
    setSentAt(Date.now());
    void queryClient.invalidateQueries({ queryKey: qk.message(id) });
    void queryClient.invalidateQueries({ queryKey: ['drafts'] });
  }

  const snooze = useMutation({
    mutationFn: (hours: number) => api.snoozeMessage(id, { hours }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Snoozed.');
      onClose();
    },
    onError: () => toast.error('Could not snooze.'),
  });

  const dismiss = useMutation({
    mutationFn: () => api.dismissMessage(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Dismissed.');
      onClose();
    },
    onError: () => toast.error('Could not dismiss.'),
  });

  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;
  const m = data.message;
  const meta = CLASSIFICATION_META[m.classification];

  return (
    <Card className="sticky top-0 flex h-[calc(100vh-3rem)] flex-col overflow-hidden">
      <CardContent className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium leading-tight">{m.subject || '(no subject)'}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {m.fromName ? `${m.fromName} <${m.fromEmail}>` : m.fromEmail} ·{' '}
              {relativeTime(m.receivedAt)}
            </p>
          </div>
          <Button size="sm" variant="ghost" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          <Button
            size="sm"
            variant="default"
            onClick={() => compose.mutate('reply')}
            disabled={compose.isPending}
          >
            <Reply className="h-3.5 w-3.5" /> Reply
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => compose.mutate('reply_all')}
            disabled={compose.isPending}
          >
            <ReplyAll className="h-3.5 w-3.5" /> Reply all
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => compose.mutate('forward')}
            disabled={compose.isPending}
          >
            <Forward className="h-3.5 w-3.5" /> Forward
          </Button>
          <SnoozeButton onSnooze={(hours) => snooze.mutate(hours)} disabled={snooze.isPending} />
          <Button
            size="sm"
            variant="outline"
            onClick={() => dismiss.mutate()}
            disabled={dismiss.isPending}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Dismiss
          </Button>
        </div>

        <div className="flex flex-wrap gap-1.5">
          <Badge variant={meta.variant}>{meta.label}</Badge>
          {m.triagePriority && <Badge variant="muted">P{m.triagePriority}</Badge>}
          {m.tags.map((t) => (
            <Badge key={t} variant="outline" className="text-[10px]">
              {TAG_LABELS[t] ?? t}
            </Badge>
          ))}
          {m.triageMethod && (
            <Badge variant="muted" className="text-[10px]">
              via {m.triageMethod}
            </Badge>
          )}
          {m.projectName && <Badge variant="outline">{m.projectName}</Badge>}
          <Badge variant="outline" className="text-[10px]">
            {m.accountEmail}
          </Badge>
        </div>

        {m.classificationReason && (
          <p className="rounded-md border border-border bg-muted/30 p-2 text-xs italic text-muted-foreground">
            {m.classificationReason}
          </p>
        )}

        <ThreadSummaryCard messageId={id} />

        <div className="rounded-md border border-border bg-background p-4">
          <EmailBody body={m.bodyPlain} />
        </div>

        {m.attachments && m.attachments.length > 0 && (
          <InboundAttachments messageId={id} attachments={m.attachments} />
        )}

        {composingDraftId && (
          <div
            ref={composerRef}
            className={cn(
              'scroll-mt-4 rounded-md border-2 bg-muted/30 p-4',
              sentAt ? 'border-emerald-500/40' : 'border-foreground/30',
            )}
          >
            <div className="mb-3 flex items-center justify-between gap-2">
              <p
                className={cn(
                  'text-[11px] font-semibold uppercase tracking-wider',
                  sentAt ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground',
                )}
              >
                {sentAt ? 'Sent — how was the draft?' : 'Composing reply'}
              </p>
              <div className="flex items-center gap-2">
                <Link
                  to={`/drafts?id=${composingDraftId}`}
                  className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                >
                  Open in Drafts <ExternalLink className="h-3 w-3" />
                </Link>
                {sentAt && (
                  <button
                    type="button"
                    onClick={dismissComposer}
                    className="inline-flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground"
                    aria-label="Dismiss"
                  >
                    <X className="h-3 w-3" /> Done
                  </button>
                )}
              </div>
            </div>
            <DraftComposer
              id={composingDraftId}
              compact
              showOriginal={false}
              onSent={markSent}
              onDiscarded={dismissComposer}
            />
          </div>
        )}

        <ContextSection
          context={contextQuery.data}
          loading={contextQuery.isLoading}
          onSelectMessage={onSelectMessage}
        />
      </CardContent>
    </Card>
  );
}

function InboundAttachments({
  messageId,
  attachments,
}: {
  messageId: string;
  attachments: import('@cc/types').MessageAttachmentMeta[];
}) {
  return (
    <div className="rounded-md border border-border bg-muted/20 p-3">
      <div className="mb-2 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        <Paperclip className="h-3 w-3" /> Attachments ({attachments.length})
      </div>
      <ul className="space-y-1">
        {attachments.map((a) => (
          <li key={a.gmailAttachmentId}>
            <a
              href={api.messageAttachmentUrl(messageId, a.gmailAttachmentId)}
              download={a.filename}
              className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-2.5 py-1.5 text-[12px] hover:border-foreground/30"
            >
              <span className="flex min-w-0 items-center gap-2">
                <Paperclip className="h-3.5 w-3.5 flex-none text-muted-foreground" />
                <span className="truncate">{a.filename}</span>
              </span>
              <span className="flex-none text-[10px] text-muted-foreground">
                {formatInboundBytes(a.sizeBytes)}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function formatInboundBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function SnoozeButton({
  onSnooze,
  disabled,
}: {
  onSnooze: (hours: number) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const options = snoozeOptions();

  return (
    <div ref={ref} className="relative">
      <Button
        size="sm"
        variant="outline"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
      >
        <Clock className="h-3.5 w-3.5" /> Snooze
      </Button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-1 w-48 overflow-hidden rounded-md border border-border bg-card shadow-md">
          {options.map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                onSnooze(o.hours);
                setOpen(false);
              }}
              className="block w-full px-3 py-2 text-left text-xs transition-colors hover:bg-accent"
            >
              <span className="font-medium">{o.label}</span>
              <span className="ml-2 text-muted-foreground">{o.note}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function snoozeOptions(): Array<{ label: string; hours: number; note: string }> {
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(7, 0, 0, 0);
  const tomorrowHours = (tomorrow.getTime() - now.getTime()) / 1000 / 3600;

  const nextMonday = new Date(now);
  const daysUntilMon = (8 - nextMonday.getDay()) % 7 || 7;
  nextMonday.setDate(nextMonday.getDate() + daysUntilMon);
  nextMonday.setHours(7, 0, 0, 0);
  const nextMondayHours = (nextMonday.getTime() - now.getTime()) / 1000 / 3600;

  return [
    { label: '1 hour', hours: 1, note: '' },
    { label: '4 hours', hours: 4, note: '' },
    { label: 'Tomorrow', hours: tomorrowHours, note: '7am' },
    { label: 'Next week', hours: nextMondayHours, note: 'Mon 7am' },
  ];
}

function ContextSection({
  context,
  loading,
  onSelectMessage,
}: {
  context: import('@cc/types').MessageContextDto | undefined;
  loading: boolean;
  onSelectMessage?: (id: string) => void;
}) {
  if (loading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (!context) return null;

  const hasAny =
    context.sameSender.length + context.sameProject.length + context.relatedDrafts.length > 0;
  if (!hasAny) return null;

  return (
    <div className="space-y-3 border-t border-border pt-3">
      {context.sameSender.length > 0 && (
        <div>
          <p className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <Mail className="h-3 w-3" /> More from this sender
          </p>
          <ul className="space-y-1">
            {context.sameSender.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  onClick={() => onSelectMessage?.(m.id)}
                  className="block w-full rounded-md border border-border/60 bg-muted/20 px-2 py-1.5 text-left transition-colors hover:border-foreground/20"
                >
                  <div className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="truncate">{m.subject || '(no subject)'}</span>
                    <span className="flex-none text-[10px] text-muted-foreground">
                      {shortDate(m.receivedAt)}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {context.sameProject.length > 0 && (
        <div>
          <p className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <FolderKanban className="h-3 w-3" /> Same project
          </p>
          <ul className="space-y-1">
            {context.sameProject.map((a) => (
              <li key={a.id}>
                <div className="rounded-md border border-border/60 bg-muted/20 px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="truncate">{a.summary}</span>
                    <span className="flex-none text-[10px] text-muted-foreground">
                      {shortDate(a.occurredAt)}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {a.source.toLowerCase().replace('_', ' ')}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {context.relatedDrafts.length > 0 && (
        <div>
          <p className="mb-2 inline-flex items-center gap-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <FileEdit className="h-3 w-3" /> Related drafts
          </p>
          <ul className="space-y-1">
            {context.relatedDrafts.map((d) => (
              <li key={d.id}>
                <Link
                  to={`/drafts?id=${d.id}`}
                  className="block rounded-md border border-border/60 bg-muted/20 px-2 py-1.5 transition-colors hover:border-foreground/20"
                >
                  <div className="flex items-center justify-between gap-2 text-[12px]">
                    <span className="truncate">{d.bodyExcerpt}</span>
                    <Sparkles className="h-3 w-3 flex-none text-muted-foreground" />
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {d.status.toLowerCase()} · updated {relativeTime(d.updatedAt)}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
